import React, {
  useState,
  Dispatch,
  RefObject,
  useEffect,
  forwardRef,
  SetStateAction,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import { Alert, Chip, Grid, TextField, Typography } from '@mui/material';

import { CalendarFilterOption } from 'types/types';
import { Ref as DateRangePickerRef } from './DateRangePicker';
import { Ref as DateRangeCalendarRef } from './DateRangeCalendar';
import { Ref as DataGranularitiesRef } from './DataGranularities';
import { getDateRange, getMonthRange, getWeekRange } from '../helper';

export interface Ref {
  enteredValues: {
    daysFrom: number;
    daysTo: number;
  };
  showCustomOptions: boolean;
  handleChangeEnteredValues: (enteredValues: {
    daysFrom: number;
    daysTo: number;
  }) => void;
  handleShowCustomOptions: (show: boolean) => void;
}

interface Props {
  customFilteredEnteredValues?: {
    daysFrom: number;
    daysTo: number;
  };
  dateRangePickerRef: RefObject<DateRangePickerRef>;
  dateRangeCalendarRef: RefObject<DateRangeCalendarRef>;
  dataGranularitiesRef: RefObject<DataGranularitiesRef>;
  setSelectedOption: Dispatch<SetStateAction<CalendarFilterOption | undefined>>;
}

const PreDefinedCalendarCustomFilter: ForwardRefRenderFunction<Ref, Props> = (
  {
    setSelectedOption,
    dateRangePickerRef,
    dateRangeCalendarRef,
    dataGranularitiesRef,
    customFilteredEnteredValues,
  },
  ref
) => {
  const [enteredValues, setEnteredValues] = useState<{
    daysFrom: number;
    daysTo: number;
  }>({ daysFrom: 0, daysTo: 0 });
  const [error, setError] = useState<string | undefined>(undefined);
  const [showCustomOptions, setShowCustomOptions] = useState<boolean>(false);

  const handleChangeEnteredValues = (enteredValues: {
    daysFrom: number;
    daysTo: number;
  }) => {
    setEnteredValues(enteredValues);
  };

  const handleShowCustomOptions = (show: boolean) => setShowCustomOptions(show);

  const handleClick = () => {
    setSelectedOption(undefined);
    setShowCustomOptions(true);
  };

  const handleCalendarDatesChange = (enteredValues: {
    daysFrom: number;
    daysTo: number;
  }) => {
    let range: { dateFrom: Date; dateTo: Date };

    const granularity = dataGranularitiesRef.current?.selectedGranularity;

    if (granularity?.value.toLowerCase() === 'weekly') {
      range = getWeekRange(enteredValues.daysFrom, enteredValues.daysTo);
    } else if (granularity?.value.toLowerCase() === 'monthly') {
      range = getMonthRange(enteredValues.daysFrom, enteredValues.daysTo);
    } else {
      range = getDateRange(enteredValues.daysFrom, enteredValues.daysTo);
    }

    dateRangePickerRef.current?.handleChangeFromAndToDates(
      range.dateFrom,
      range.dateTo
    );
    dateRangeCalendarRef.current?.handleChangeFromAndToDates(
      range.dateFrom,
      range.dateTo
    );
  };

  const handleChange = (event: any, dateSource: 'From' | 'To') => {
    const clonedEnteredValues = { ...enteredValues };

    if (dateSource === 'From') {
      clonedEnteredValues.daysFrom = event.target.value;
    } else if (dateSource === 'To') {
      clonedEnteredValues.daysTo = event.target.value;
    }

    setEnteredValues(clonedEnteredValues);

    if (
      clonedEnteredValues.daysTo < 0 ||
      clonedEnteredValues.daysFrom < 0 ||
      clonedEnteredValues.daysTo > clonedEnteredValues.daysFrom
    ) {
      setError('Invalid date range');
      return;
    } else {
      setError(undefined);
    }
    handleCalendarDatesChange(clonedEnteredValues);
  };

  useImperativeHandle(ref, () => ({
    enteredValues,
    showCustomOptions,
    handleShowCustomOptions,
    handleChangeEnteredValues,
  }));

  useEffect(() => {
    if (customFilteredEnteredValues) {
      setEnteredValues({
        daysFrom: customFilteredEnteredValues.daysFrom,
        daysTo: customFilteredEnteredValues.daysTo,
      });
      handleCalendarDatesChange(customFilteredEnteredValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFilteredEnteredValues]);

  return (
    <React.Fragment>
      <Grid item xs={3}>
        <Chip
          label={'Custom'}
          onClick={handleClick}
          className="calendar-filter-option"
          sx={{
            backgroundColor: showCustomOptions ? '#ffe1c6 !important' : '',
          }}
        />
      </Grid>
      <Grid item xs={12}>
        {showCustomOptions && (
          <Grid
            container
            className="custom-calendar-filters-container"
            sx={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Grid item xs={2} sx={{ textAlign: 'center' }}>
              <Typography>From</Typography>
            </Grid>
            <Grid item xs={3}>
              <TextField
                size="small"
                type="number"
                value={enteredValues.daysFrom}
                InputProps={{ inputProps: { min: 0 } }}
                onChange={(event: any) => handleChange(event, 'From')}
              />
            </Grid>
            <Grid item xs={2} sx={{ textAlign: 'center' }}>
              <Typography>To</Typography>
            </Grid>
            <Grid item xs={3}>
              <TextField
                size="small"
                type="number"
                value={enteredValues.daysTo}
                InputProps={{ inputProps: { min: 0 } }}
                onChange={(event: any) => handleChange(event, 'To')}
              />
            </Grid>
            <Grid item xs={2} sx={{ textAlign: 'center' }}>
              <Typography>Ago</Typography>
            </Grid>
            {error && (
              <Grid item xs={12} sx={{ marginTop: '1rem' }}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </React.Fragment>
  );
};

export default forwardRef<Ref, Props>(PreDefinedCalendarCustomFilter);
